import StorageService from "../services/StorageService.js";
import MapInteraction from "../utils/MapInteraction.js";
import SoundManager from "../utils/SoundManager.js";
import FeedbackEffects from "../utils/FeedbackEffects.js";
import MapExploration from "../utils/MapExploration.js";
import { getFlagPath, renderFlagDuelError } from "../utils/FlagUtils.js";
import BaseGame from "./BaseGame.js";

/** Temps laissé au joueur pour voir le pays révélé après une erreur, en ms. */
const REVEAL_DELAY = 1500;

export default class MapNerdClassic extends BaseGame {
	/**
	 * @param {string|null} continent Continent code (af/as/eu/na/sa/oc) to scope
	 *   the game to, or null for the whole world. The scope is infixed into the
	 *   gameId (`mapnerdclassic.af`), so lives/best/resume persist per scope while
	 *   the world keys stay backward-compatible.
	 */
	constructor(continent = null) {
		const gameId = continent ? `mapnerdclassic.${continent}` : "mapnerdclassic";
		super(gameId, '.map-nerd-classic', {
			gameNameKey: "home.mapNerd",
			modeLabelKey: "home.classic",
			mode: "classic",
			shareEmoji: "🟦",
			slug: "map-nerd-classic"
		});
		if (!this.page) return;

		this.continent = continent;
		this.scopedPool = continent ? (geoNerdApp.easyCountriesByContinent[continent] || []) : geoNerdApp.easyCountries;

		this.mapContainer = this.page.querySelector(".map-container");
		this.map = this.mapContainer.querySelector("svg");
		this.countryToGuess = this.page.querySelector(".country-to-guess");
		this.countryToGuessName = this.countryToGuess.querySelector(".name");
		this.countryToGuessFlag = this.countryToGuess.querySelector(".flag-container");
		this.countries = this.map.querySelectorAll("path");

		this.mapInteraction = new MapInteraction(this.mapContainer, this.map);
		this.buildClickEvent();
		this.infiniteButton();
		this.init();

		this.isInfinite = false;
	}

	init() {
		this.initCountries(this.scopedPool);
		this.startClock();
		this.newProposal();
		this.reloadButton();
		this.initShare();
		this.updateHeartsUI();
		this.updateProgress();
	}

	initCountries(defaultList) {
		super.initCountries(defaultList);

		// Only the scoped pool is playable; mark every other easy country as
		// out-of-scope (dimmed, non-clickable) so the focus stays on the continent.
		const scopeCodes = new Set(this.scopedPool.map(c => c.code));
		geoNerdApp.easyCountries.forEach(c => {
			const path = this.map.querySelector(`#${c.code}`);
			if (!path) return;
			if (scopeCodes.has(c.code)) {
				path.classList.add("onu");
				if (!this.countriesLeft.find(left => left.code === c.code)) {
					path.classList.add("guessed");
				}
			} else {
				path.classList.add("out-of-scope");
			}
		});
	}

	buildClickEvent() {
		this.countries.forEach(country => {
			country.addEventListener("click", () => {
				if (!this.mapInteraction.dragged && this.canGuess && country.classList.contains("onu") && !country.classList.contains("guessed")) {
					this.guessOnMap(country.id);
				}
			});
		});
	}

	newProposal() {
		if (this.destroyed) return;
		if (this.countriesLeft.length === 0) {
			this.finishGame();
			return;
		}

		gsap.to(this.countryToGuess, { opacity: 1 });
		this.countryToGuess.classList.remove("valid", "invalid");

		let current = StorageService.load(`${this.gameId}.current`);
		if (!current || current.found) {
			this.rightAnswer = this.drawQuestion();
			StorageService.save(`${this.gameId}.current`, this.rightAnswer);
		} else {
			this.rightAnswer = current;
			const match = geoNerdApp.easyCountries.find(c => c.code === this.rightAnswer.code);
			if (match) this.rightAnswer.name = match.name;
		}

		this.countryToGuessName.innerHTML = this.rightAnswer.name;
		this.countryToGuessFlag.innerHTML = `<div class="flag" style="background-image: url(${getFlagPath(this.rightAnswer.code)})"></div>`;
	}

	guessOnMap(countryCode) {
		if (this.destroyed) return;
		this.canGuess = false;
		const isCorrect = countryCode === this.rightAnswer.code;

		if (!isCorrect) {
			this.recordError({
				countryName: this.rightAnswer.name,
				correctCode: this.rightAnswer.code,
				guessedCode: countryCode
			});
		}

		if (isCorrect) {
			SoundManager.correct();
			FeedbackEffects.confetti(this.countryToGuess);
			this.countryToGuess.classList.add("valid");
			const path = this.map.querySelector(`#${countryCode}`);
			if (path) path.classList.add("guessed");

			// Permanently record this country as "explored" (cross-scope, cross-language).
			MapExploration.record(this.rightAnswer.code);

			this.countriesLeft = this.countriesLeft.filter(elem => elem.code !== this.rightAnswer.code);
			StorageService.save(`${this.gameId}.current`, { found: true });
			this.updateStorage();
			this.updateProgress();
		} else {
			SoundManager.wrong();
			FeedbackEffects.shake(this.countryToGuess);
			this.countryToGuess.classList.add("invalid");
			const path = this.map.querySelector(`#${countryCode}`);
			if (path) {
				path.classList.add("invalid-flash");
				setTimeout(() => path.classList.remove("invalid-flash"), 500);
			}
			this.revealCorrectCountry();
			// Sans ça, `newProposal()` retombait sur ce même pays (le `current`
			// persisté n'est marqué trouvé qu'en cas de bonne réponse) : une erreur
			// forçait à retenter le pays raté au lieu d'en tirer un autre au sort.
			StorageService.remove(`${this.gameId}.current`);
		}

		// Une erreur laisse le temps de voir où était le pays (cf. revealCorrectCountry) ;
		// une bonne réponse enchaîne au rythme habituel.
		const delay = isCorrect ? 600 : REVEAL_DELAY;

		if (this.isInfinite || isCorrect) {
			setTimeout(() => {
				this.newProposal();
				this.canGuess = true;
			}, delay);
		} else {
			this.updateLife(true);
			if (this.lifes > 0) {
				setTimeout(() => {
					this.newProposal();
					this.canGuess = true;
				}, delay);
			}
		}
	}

	/**
	 * Montre le pays cherché après une erreur.
	 *
	 * Jusqu'ici, se tromper faisait clignoter le pays cliqué puis enchaînait : la
	 * bonne position n'était jamais montrée, sauf pour le tout dernier pays à la
	 * fin de la partie. On ne pouvait donc pas apprendre de ses erreurs, ce qui
	 * est pourtant l'intérêt du mode. La vue ne bouge pas (un recadrage
	 * automatique désorienterait au milieu d'une partie) : le pays est simplement
	 * mis en évidence là où il est.
	 */
	revealCorrectCountry() {
		const path = this.map.querySelector(`#${this.rightAnswer.code}`);
		if (!path) return;
		path.classList.add("revealed-answer");
		setTimeout(() => path.classList.remove("revealed-answer"), REVEAL_DELAY - 100);
	}

	updateLife(decrease) {
		if (this.isInfinite) {
			this.updateProgress();
			return;
		}

		if (decrease) {
			this.lifes--;
			StorageService.save(`${this.gameId}.lifes`, this.lifes);
		}

		this.updateHeartsUI();

		if (this.lifes <= 0) {
			SoundManager.gameOver();
			this.lifes = 0;
			StorageService.save(`${this.gameId}.lifes`, 0);
			this.recordLoss();

			const correctPath = this.map.querySelector(`#${this.rightAnswer.code}`);
			if (correctPath) {
				correctPath.classList.add("correct-answer");
				this.mapInteraction.zoomToElement(correctPath, null, 2);
			}

			this.revealRecapAfterDelay(() => {
				this.renderEndGameStats(this.looseMessage);
				this.renderErrors(this.looseMessage);
				this.looseMessage.classList.add("show");
				this.page.querySelector(".progress-container").style.display = "none";
				if (correctPath) correctPath.classList.remove("correct-answer");
			});
		}
		this.updateProgress();
	}

	/**
	 * Défère `reveal` de 2 s, le temps de voir où était le pays sur la carte
	 * avant de passer au récap. Le bouton "Recommencer" disparaît dans l'instant :
	 * laissé visible, il restait cliquable pendant ce délai et réinitialisait la
	 * partie au lieu de montrer le récap. La sauvegarde (best/stats) a déjà eu
	 * lieu au moment de cet appel.
	 */
	revealRecapAfterDelay(reveal) {
		const restartBtn = this.page.querySelector(".btn-restart-ingame");
		if (restartBtn) restartBtn.style.display = "none";
		setTimeout(reveal, 2000);
	}

	infiniteButton() {
		const btn = this.page.querySelector(".btn.infinite");
		const quitBtn = this.page.querySelector(".btn.infinite-quit");

		if (btn) {
			btn.addEventListener("click", () => {
				this.isInfinite = true;
				this.looseMessage.classList.remove("show");
				this.page.querySelector(".life-remaining").style.display = "none";
				this.page.querySelector(".btn-restart-ingame").style.display = "none";
				this.page.querySelector(".progress-container").style.display = "flex";
				this.page.querySelector(".progress-container").style.pointerEvents = "all";
				this.page.querySelector(".infinite-actions").style.display = "flex";

				if (!this.page.querySelector(".infinite-label-added")) {
					this.page.querySelector(".progress").insertAdjacentHTML("beforeend", `<span class="infinite-label-added">&nbsp;-&nbsp;${window.geoNerdApp.languageManager.t('game.infiniteMode')}</span>`);
				}

				this.newProposal();
				this.canGuess = true;
			});
		}

		if (quitBtn) {
			quitBtn.addEventListener("click", () => {
				window.location.href = window.geoNerdApp.languageManager.currentLang === "en" ? "/en/" : "/";
			});
		}
	}

	renderErrorItem(error) {
		return renderFlagDuelError(error);
	}

	finishGame() {
		SoundManager.victory();
		this.mapInteraction.destroy();

		// En mode Infini, le score a déjà été sauvegardé à la perte des vies —
		// on ne recalcule pas les stats, seulement l'affichage.
		if (!this.isInfinite) this.recordWin();
		// Le score reste affiché pendant le délai avant le récap (au lieu de
		// disparaître aussitôt) : sans ce rafraîchissement, "MEILLEUR" resterait
		// à l'ancienne valeur pendant toute cette attente.
		this.updateProgress();

		this.revealRecapAfterDelay(() => {
			this.mapContainer.remove();
			this.countryToGuess.remove();
			if (this.isInfinite) {
				this.renderEndGameStats(this.winMessage);
				this.winMessage.classList.add("show");
				if (this.progressContainer) this.progressContainer.style.display = "none";
			} else {
				this.showWinMessage();
			}
		});
	}

	/**
	 * Detach the game when the controller switches scope: kill the map's
	 * window-level listeners/animations and stop any pending turn callbacks from
	 * mutating the (about-to-be-replaced) DOM.
	 */
	teardown() {
		this.destroyed = true;
		this.canGuess = false;
		if (this.mapInteraction) this.mapInteraction.destroy();
	}
}
